import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ARTIFACT_ACCEPT,
  getSolution,
  signSolutionUpload,
  submitSolution,
} from "../services/evaluatorService";

interface Props {
  sessionId: string;
  value: string;
  onChange: (v: string) => void;
  getToken: () => string | undefined;
}

const AnswerPanel: React.FC<Props> = ({
  sessionId,
  value,
  onChange,
  getToken,
}) => {
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const fileInput = useRef<HTMLInputElement>(null);
  // The panel's own copy invites the learner to navigate away while grading
  // ("it will finish without you"). If they take it up on that, the POST
  // below still resolves after the component is gone — this stops the
  // resulting navigate() from yanking them back from wherever they went. The
  // grade itself is unaffected; only the redirect is skipped.
  const mountedRef = useRef(true);

  const [artifactKey, setArtifactKey] = useState<string | null>(null);
  const [artifactName, setArtifactName] = useState<string | null>(null);
  const [hasReportCard, setHasReportCard] = useState(false);
  // null means "not known yet" — the fetch hasn't resolved. Treated the same
  // as false for disabling Submit, but must not show the "no rubric" message
  // until we actually know that's true; getSolution maps a 404 (missing
  // session) to null too, so this also covers that abnormal case.
  const [gradable, setGradable] = useState<boolean | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getSolution(sessionId, getToken())
      .then((solution) => {
        if (cancelled || !solution) return;
        if (solution.answer) onChange(solution.answer);
        if (solution.artifact) {
          setArtifactKey(solution.artifact);
          setArtifactName(solution.artifact.split("/").pop() ?? solution.artifact);
        }
        setHasReportCard(solution.hasReportCard);
        setGradable(solution.gradable);
      })
      .catch((err) => {
        // getSolution resolves null for "nothing submitted yet" — that's the
        // normal first visit and isn't an error. A rejection here is always
        // something real (a 401, a 500, a network failure) and must not be
        // silent, or the learner is left staring at a panel that never
        // finished loading with no explanation why.
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Failed to load your previous submission",
        );
      });
    return () => {
      cancelled = true;
    };
    // Deliberately keyed on the session alone: re-running when onChange or the
    // token getter is re-created would clobber the learner's in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const key = await signSolutionUpload(sessionId, file, getToken());
      // One artifact per solution: a new upload replaces whatever was attached.
      setArtifactKey(key);
      setArtifactName(file.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await submitSolution(
        sessionId,
        {
          answer: value.trim() || undefined,
          artifact: artifactKey ?? undefined,
        },
        getToken(),
      );
      // The grade is written either way; only the redirect is conditional —
      // firing it after the learner has already navigated elsewhere would
      // yank them back to the report card from wherever they went.
      if (mountedRef.current) {
        navigate(`/${orgSlug}/sessions/${sessionId}/report-card`);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : "Submission failed");
        setSubmitting(false);
      }
    }
  };

  const nothingToSubmit = !value.trim() && !artifactKey;
  const disabled = !gradable || nothingToSubmit || uploading || submitting;

  return (
    <div className="flex h-full flex-col space-y-4 p-6">
      <div className="flex items-center gap-2">
        <span className="material-symbols-outlined text-sm text-tertiary">assignment</span>
        <h3 className="font-label text-xs font-bold uppercase tracking-[0.2em] text-tertiary">
          Submit Work
        </h3>
      </div>

      {submitting ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <span className="material-symbols-outlined animate-spin text-3xl text-primary">
            progress_activity
          </span>
          <p className="font-label text-xs uppercase tracking-widest text-on-surface">
            Generating report card
          </p>
          <p className="max-w-[16rem] font-body text-xs leading-relaxed text-slate-500">
            Grading your conversation and submission against the rubric. This usually takes
            under a minute. If you navigate away it will finish without you — reopen this
            panel to pick the report card up.
          </p>
        </div>
      ) : (
        <>
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Write your final answer here..."
            className="w-full flex-1 resize-none rounded-lg border border-outline-variant/20 bg-surface-container-low p-3 font-body text-sm text-on-surface placeholder-slate-500 focus:border-tertiary/50 focus:outline-none focus:ring-1 focus:ring-tertiary/30"
          />

          <div className="shrink-0">
            <p className="font-label text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Supporting document <span className="text-slate-500">— optional</span>
            </p>
            {artifactName ? (
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 py-2.5 text-xs text-slate-300">
                <span className="material-symbols-outlined text-base text-tertiary">
                  attach_file
                </span>
                <span className="min-w-0 truncate" title={artifactName}>
                  {artifactName}
                </span>
                <button
                  type="button"
                  title="Remove this document"
                  className="ml-auto shrink-0 text-slate-400 transition-colors hover:text-error"
                  onClick={() => {
                    setArtifactKey(null);
                    setArtifactName(null);
                  }}
                >
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              </div>
            ) : (
              /* A bordered drop-zone rather than a text link. The first version
                 was bare slate-400 text sitting directly above the filled
                 Submit button, and read as a caption — people looked at this
                 panel and reported there was no way to attach anything. */
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileInput.current?.click()}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-outline-variant/40 bg-surface-container-low/50 px-3 py-3 text-xs text-slate-300 transition-colors hover:border-tertiary/50 hover:bg-surface-container-low hover:text-on-surface disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-base text-tertiary">
                  {uploading ? "progress_activity" : "upload_file"}
                </span>
                {uploading ? "Uploading…" : "Attach a PDF or image"}
              </button>
            )}
            <input
              ref={fileInput}
              type="file"
              accept={ARTIFACT_ACCEPT}
              className="hidden"
              onChange={handleFile}
            />
          </div>

          {gradable === false && (
            <p className="shrink-0 font-body text-xs leading-relaxed text-slate-500">
              This session was started without a rubric, so it can&apos;t be graded. You can
              still draft your answer here.
            </p>
          )}

          {error && <p className="shrink-0 text-xs text-error">{error}</p>}

          <button
            type="button"
            disabled={disabled}
            onClick={handleSubmit}
            className="shrink-0 rounded-lg border border-primary/30 bg-primary/15 px-4 py-2 font-label text-sm font-bold uppercase tracking-wider text-primary transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
          >
            Request Evaluation
          </button>

          {hasReportCard && (
            <button
              type="button"
              onClick={() => navigate(`/${orgSlug}/sessions/${sessionId}/report-card`)}
              className="shrink-0 font-label text-xs uppercase tracking-wider text-tertiary hover:text-on-surface"
            >
              View report card
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default AnswerPanel;
